import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, FileText, Percent, Globe } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What should a Malaysian invoice include?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A Malaysian invoice should include: your business name and address; your SST (Sales and Service Tax) registration number if applicable; the client name and address; a unique invoice number; the invoice date; a description of goods or services; the amount; and your payment details. SST-registered businesses must show the applicable tax rate and tax amount separately.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is SST in Malaysia and what is the rate?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Malaysia replaced GST with SST (Sales and Service Tax) in 2018. The Service Tax rate is 8% (increased from 6% in 2024) for most taxable services, and 6% for specific services such as telecommunications, parking, and logistics. Sales Tax on goods is 5% or 10% depending on the type of goods. SST registration is required for service providers with annual taxable services turnover exceeding MYR 500,000.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do Malaysian freelancers invoice international clients?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Malaysian freelancers providing services to overseas clients typically invoice in USD, GBP, or SGD. Export of services to foreign clients is generally not subject to Service Tax. Use the international invoice type to bill in any currency with your Malaysian bank details. Many Malaysian freelancers receive international payments via Wise, PayPal, or bank wire transfer.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is e-invoicing in Malaysia and does it affect me?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Malaysia is rolling out mandatory e-invoicing through LHDN (Inland Revenue Board). From August 2024, businesses with annual turnover exceeding MYR 100 million must issue e-invoices. The mandate will extend to all businesses by 2027. For small businesses and freelancers, Invilic allows you to create and download clean PDF invoices while you plan your e-invoicing transition.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is this invoice generator free for Malaysian users?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Invilic is completely free for Malaysian freelancers and businesses. Create professional invoices in USD, MYR, SGD, or any currency. Download a clean PDF instantly — no sign-up required for up to 5 invoices per month.',
      },
    },
  ],
};

const invoiceTypes = [
  {
    icon: Percent,
    title: 'SST Invoice',
    subtitle: 'For SST-registered Malaysian businesses',
    features: ['8% Service Tax auto-calculated', 'SST registration number field', 'Net + SST + total', 'LHDN-ready format', 'MYR currency'],
    to: '/create/vat',
    color: 'blue',
    btnClass: 'bg-blue-600 hover:bg-blue-700',
  },
  {
    icon: FileText,
    title: 'Standard Invoice',
    subtitle: 'For businesses below MYR 500,000',
    features: ['No SST required', 'MYR currency', 'Custom line items', 'Logo upload', 'Payment terms'],
    to: '/create/freelancer',
    color: 'slate',
    btnClass: 'bg-slate-700 hover:bg-slate-800',
  },
  {
    icon: Globe,
    title: 'International Invoice',
    subtitle: 'For billing overseas clients',
    features: ['USD, GBP, SGD billing', 'Export service format', 'SWIFT / bank details', 'Multi-currency support', 'Professional PDF'],
    to: '/create/international',
    color: 'indigo',
    btnClass: 'bg-indigo-600 hover:bg-indigo-700',
  },
];

export default function InvoiceGeneratorMalaysiaPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="Invoice Generator Malaysia: Free SST Invoice Maker | Invilic"
        description="Free invoice generator for Malaysian businesses and freelancers. Create SST invoices or standard invoices in MYR. PDF download free, no sign-up required."
        canonical="https://www.invilic.com/invoice-generator-malaysia"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-16">
          <span className="inline-block bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
            🇲🇾 Malaysia
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
            Invoice Generator Malaysia
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            Free invoice generator for Malaysian businesses, freelancers, and sole traders. Create SST tax invoices with 8% Service Tax auto-calculated, or standard invoices in MYR. Download professional PDF invoices instantly.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/create/vat" className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-4 rounded-xl shadow-lg shadow-blue-600/20 transition-all duration-200">
              Create SST Invoice <ArrowRight className="w-5 h-5" />
            </Link>
            <Link to="/create/freelancer" className="inline-flex items-center justify-center gap-2 bg-slate-700 hover:bg-slate-800 text-white font-semibold px-6 py-4 rounded-xl transition-all duration-200">
              Create Standard Invoice <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">Free · No sign-up · PDF in seconds</p>
        </div>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">Which Invoice Do You Need?</h2>
          <div className="grid sm:grid-cols-3 gap-5">
            {invoiceTypes.map(({ icon: Icon, title, subtitle, features, to, color, btnClass }) => (
              <div key={title} className={`border border-${color}-100 dark:border-${color}-800/30 rounded-2xl p-6 bg-${color}-50 dark:bg-${color}-900/10`}>
                <div className={`w-10 h-10 rounded-xl bg-${color}-100 dark:bg-${color}-900/40 flex items-center justify-center mb-4`}>
                  <Icon className={`w-5 h-5 text-${color}-600 dark:text-${color}-400`} />
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-1">{title}</h3>
                <p className={`text-xs text-${color}-700 dark:text-${color}-300 mb-4`}>{subtitle}</p>
                <ul className="space-y-2 mb-5">
                  {features.map(f => (
                    <li key={f} className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                      <CheckCircle className={`w-3.5 h-3.5 text-${color}-500 flex-shrink-0`} /> {f}
                    </li>
                  ))}
                </ul>
                <Link to={to} className={`inline-flex items-center justify-center gap-1.5 w-full px-4 py-2.5 ${btnClass} text-white text-sm font-semibold rounded-xl transition-all`}>
                  Create Free <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16 p-8 rounded-2xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-5">SST Invoice vs Standard Invoice in Malaysia</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <div className="font-semibold text-blue-700 dark:text-blue-400 mb-3">Use an SST Invoice if:</div>
              {[
                'Your annual taxable services turnover exceeds MYR 500,000',
                'You are registered for Service Tax with Royal Malaysian Customs',
                'Your client needs a tax invoice showing SST breakdown',
              ].map(p => (
                <div key={p} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 mb-2">
                  <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" /> {p}
                </div>
              ))}
            </div>
            <div>
              <div className="font-semibold text-slate-700 dark:text-slate-400 mb-3">Use a Standard Invoice if:</div>
              {[
                'Your taxable services turnover is below MYR 500,000',
                'You are a freelancer or sole trader not SST-registered',
                'You are billing an overseas client (export — no SST applies)',
              ].map(p => (
                <div key={p} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 mb-2">
                  <CheckCircle className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" /> {p}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">Malaysia Invoice Generator: Frequently Asked Questions</h2>
          <div className="space-y-4">
            {schema.mainEntity.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">{acceptedAnswer.text}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="text-center py-12 border-t border-gray-100 dark:border-gray-800">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Create Your Malaysian Invoice for Free</h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">Professional PDF for Malaysian businesses in under 2 minutes. No account required.</p>
          <Link to="/create/vat" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-blue-600/20 transition-all duration-200 text-lg">
            Create Invoice Free <ArrowRight className="w-5 h-5" />
          </Link>
        </section>
      </main>
    </div>
  );
}
